<script setup>
import { computed, ref } from 'vue';
import BaseBubble from 'next/message/bubbles/Base.vue';
import FormattedContent from './FormattedContent.vue';
import AttachmentChips from 'next/message/chips/AttachmentChips.vue';
import TranslationToggle from 'dashboard/components-next/message/TranslationToggle.vue';
import { MESSAGE_TYPES } from '../../constants';
import { MESSAGE_STATUS } from 'shared/constants/messages';
import { useMessageContext } from '../../provider.js';
import { useTranslations } from 'dashboard/composables/useTranslations';
import { useAccount } from 'dashboard/composables/useAccount';
import {
  DV_ACCOUNT_ID,
  assistantNoteFromAttributes,
  clientDossierFromAttributes,
  staffAgentDraftFromNote,
} from 'dashboard/helper/dvNotes';
import DvAssistantDraft from './DvAssistantDraft.vue';
import DvClientDossier from './DvClientDossier.vue';

const { content, attachments, contentAttributes, messageType, isPrivate, status } =
  useMessageContext();
const { accountId } = useAccount();

// DV: assistant draft notes in account 1 render as an insert/copy card.
const dvAssistantDraft = computed(() => {
  if (accountId.value !== DV_ACCOUNT_ID || !isPrivate?.value) return null;
  return assistantNoteFromAttributes(contentAttributes.value);
});

// DV: staff agent drafts (Agent API) get the same card, with author and summary.
const dvStaffAgentDraft = computed(() => {
  if (accountId.value !== DV_ACCOUNT_ID || !isPrivate?.value) return null;
  if (dvAssistantDraft.value) return null;
  return staffAgentDraftFromNote(contentAttributes.value, content.value);
});

const dvStaffAgentDetails = computed(() => {
  const draft = dvStaffAgentDraft.value;
  if (!draft) return [];
  return [
    ['Подготовил', draft.operator],
    ['Когда', draft.generatedAt ? new Date(draft.generatedAt).toLocaleString('ru-RU') : ''],
    ['Модель', draft.model],
    ['Источник', draft.source],
    ['Риски', (draft.riskFlags || []).join(', ')],
  ].filter(([, value]) => value);
});

const dvClientDossier = computed(() => {
  if (accountId.value !== DV_ACCOUNT_ID || !isPrivate?.value) return null;
  return clientDossierFromAttributes(contentAttributes.value);
});

const { hasTranslations, translationContent } =
  useTranslations(contentAttributes);

const renderOriginal = ref(false);

const renderContent = computed(() => {
  if (renderOriginal.value) {
    return content.value;
  }

  if (hasTranslations.value) {
    return translationContent.value;
  }

  return content.value;
});

const isTemplate = computed(() => {
  return messageType.value === MESSAGE_TYPES.TEMPLATE;
});

const contactInfoRequestState = computed(() => {
  if (status.value === MESSAGE_STATUS.FAILED) return null;

  return contentAttributes.value?.whatsappContactInfo?.state;
});

const isEmpty = computed(() => {
  return !content.value && !attachments.value?.length;
});

const handleSeeOriginal = () => {
  renderOriginal.value = !renderOriginal.value;
};
</script>

<template>
  <BaseBubble class="px-4 py-3" data-bubble-name="text">
    <div class="gap-3 flex flex-col">
      <span v-if="isEmpty" class="text-n-slate-11">
        {{ $t('CONVERSATION.NO_CONTENT') }}
      </span>
      <DvClientDossier v-if="dvClientDossier" :dossier="dvClientDossier" />
      <DvAssistantDraft
        v-else-if="dvAssistantDraft"
        :draft="dvAssistantDraft.draft"
        :model="dvAssistantDraft.model"
        :intent="dvAssistantDraft.intent"
        :content="content"
      />
      <DvAssistantDraft
        v-else-if="dvStaffAgentDraft"
        :draft="dvStaffAgentDraft.draft"
        :model="dvStaffAgentDraft.model"
        :author="dvStaffAgentDraft.operator || 'агент'"
        :summary="dvStaffAgentDraft.summary"
        :findings="dvStaffAgentDraft.findings"
        :details="dvStaffAgentDetails"
      />
      <FormattedContent
        v-else-if="renderContent"
        :content="renderContent"
      />
      <span
        v-if="contactInfoRequestState === 'pending'"
        class="text-xs font-medium text-n-slate-11"
      >
        {{ $t('CONVERSATION.REQUEST_CONTACT_INFO.STATES.PENDING') }}
      </span>
      <span
        v-else-if="contactInfoRequestState === 'shared'"
        class="text-xs font-medium text-n-slate-11"
      >
        {{ $t('CONVERSATION.REQUEST_CONTACT_INFO.STATES.SHARED') }}
      </span>
      <span
        v-else-if="contactInfoRequestState === 'identity_conflict'"
        class="text-xs font-medium text-n-slate-11"
      >
        {{ $t('CONVERSATION.REQUEST_CONTACT_INFO.STATES.IDENTITY_CONFLICT') }}
      </span>
      <TranslationToggle
        v-if="hasTranslations"
        class="-mt-3"
        :showing-original="renderOriginal"
        @toggle="handleSeeOriginal"
      />
      <AttachmentChips :attachments="attachments" class="gap-2" />
      <template v-if="isTemplate">
        <div
          v-if="contentAttributes.submittedEmail"
          class="px-2 py-1 rounded-lg bg-n-alpha-3"
        >
          {{ contentAttributes.submittedEmail }}
        </div>
      </template>
    </div>
  </BaseBubble>
</template>

<style>
p:last-child {
  margin-bottom: 0;
}
</style>
